/**
 * Utility for Firebase Functions endpoints
 * Automatically switches between local emulator and production based on NODE_ENV
 *
 * To force emulator usage even in production builds, set REACT_APP_USE_EMULATOR=true
 * in your .env file or environment variables.
 */

const FIREBASE_PROJECT_ID = 'linguabuddy-9d88f';
const FIREBASE_REGION = 'us-central1';
const LOCAL_EMULATOR_PORT = 5001;

/**
 * Check if we're in debug mode based on URL containing "d1q1chclchjxl4"
 * This is used to test optimized endpoints without affecting production
 */
export function isDebugMode(): boolean {
  console.log(window.location.href);
  if (typeof window === 'undefined') return false;
  return (
    window.location.href.includes('d2ixwlsvif2s2i') || window.location.href.includes('localhost')
  );
}

/**
 * Log debug mode status (call once on app init)
 */
export function logDebugModeStatus(): void {
  if (isDebugMode()) {
    console.log('🐛 DEBUG MODE ENABLED');
  }
}

/**
 * True when the page itself (not just the Functions backend) is being served by a local
 * dev webserver rather than the deployed production build — i.e. the Flutter webview is
 * pointed at a local React dev server. Used to gate dev-only UI (e.g. the demo-subscription
 * badge) so it can never appear for real users hitting the production site.
 */
export function isLocalWebServer(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '10.0.2.2';
}

/**
 * Get the host to use for the local Functions emulator.
 * When the app is loaded in an Android emulator WebView, the page URL is
 * http://10.0.2.2:3000 (10.0.2.2 = host from emulator). Requests to 127.0.0.1
 * from inside the emulator go to the emulator itself, not the host, so we must
 * use 10.0.2.2 for the emulator URL when running in the emulator.
 */
function getLocalEmulatorHost(): string {
  if (typeof window === 'undefined') return '127.0.0.1';
  const hostname = window.location.hostname;
  // Android emulator loads the app from 10.0.2.2; use same host for functions
  if (hostname === '10.0.2.2') return '10.0.2.2';
  return '127.0.0.1';
}

/**
 * Get the base URL for Firebase Functions
 * - Development: http://{host}:5001/{projectId}/{region} (host = 10.0.2.2 in Android emulator WebView, else 127.0.0.1)
 * - Production: https://{region}-{projectId}.cloudfunctions.net
 *
 * Can be overridden with REACT_APP_USE_EMULATOR environment variable
 */
export function getFirebaseFunctionsBaseUrl(): string {
  const emulatorFlag = process.env.REACT_APP_USE_EMULATOR;
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Use emulator when explicitly enabled, or in dev mode unless explicitly disabled
  const useEmulator = emulatorFlag === 'true' || (isDevelopment && emulatorFlag !== 'false');

  if (useEmulator) {
    const host = getLocalEmulatorHost();
    return `http://${host}:${LOCAL_EMULATOR_PORT}/${FIREBASE_PROJECT_ID}/${FIREBASE_REGION}`;
  }

  return `https://${FIREBASE_REGION}-${FIREBASE_PROJECT_ID}.cloudfunctions.net`;
}

/**
 * Get the full URL for a Firebase Function
 * @param functionName - Name of the Firebase Function
 */
export function getFirebaseFunctionUrl(functionName: string): string {
  return `${getFirebaseFunctionsBaseUrl()}/${functionName}`;
}

const FLOW_LOG_PREFIX = '[Flow]';

/**
 * Call a Firebase Cloud Function with flow logging (webapp → function → webapp).
 * Use this instead of raw fetch() to see request/response flow in the console.
 */
export async function callCloudFunction(
  name: string,
  url: string,
  init?: RequestInit,
): Promise<Response> {
  const method = init?.method ?? 'GET';
  console.log(`${FLOW_LOG_PREFIX} Webapp → ${name}`, { url, method });
  const start = Date.now();
  const res = await fetch(url, init);
  const ms = Date.now() - start;
  console.log(`${FLOW_LOG_PREFIX} Webapp ← ${name}`, {
    status: res.status,
    statusText: res.statusText,
    ms,
  });
  return res;
}

// Pre-configured function URLs
export const FIREBASE_FUNCTIONS = {
  rabbiLogin: () => getFirebaseFunctionUrl('rabbiLogin'),
  getLinkedRabbis: () => getFirebaseFunctionUrl('getLinkedRabbis'),
  uploadRabbiAudio: () => getFirebaseFunctionUrl('uploadRabbiAudio'),
  deleteRabbiAudio: () => getFirebaseFunctionUrl('deleteRabbiAudio'),
  checkRabbiAudioExists: () => getFirebaseFunctionUrl('checkRabbiAudioExists'),
  listRabbiAudioFiles: () => getFirebaseFunctionUrl('listRabbiAudioFiles'),
  wordDetect: () => getFirebaseFunctionUrl('wordDetect'),
  hello: () => getFirebaseFunctionUrl('hello'),
  getAllRabbies: () => getFirebaseFunctionUrl('getAllRabbiesList'),
  addRabbi: () => getFirebaseFunctionUrl('addRabbiEndpoint'),
  updateRabbiStatus: () => getFirebaseFunctionUrl('updateRabbiStatusEndpoint'),
  deleteRabbi: () => getFirebaseFunctionUrl('deleteRabbiEndpoint'),
  checkRabbiByEmail: () => getFirebaseFunctionUrl('checkRabbiByEmail'),
  // Audio comparison endpoints
  compareAudio: () => getFirebaseFunctionUrl('compare_audio'),
  compareAudioGraph: () => getFirebaseFunctionUrl('compare_audio_graph'),
  comparePasuk: () => getFirebaseFunctionUrl('comparePasuk'),
  getPasukExplanation: () => getFirebaseFunctionUrl('getPasukExplanation'),
  uploadUserAudio: () => getFirebaseFunctionUrl('uploadUserAudio'),
  checkUserAudioExists: () => getFirebaseFunctionUrl('checkUserAudioExists'),
  deleteUserAudio: () => getFirebaseFunctionUrl('deleteUserAudio'),
  validatePurchase: () => getFirebaseFunctionUrl('validatePurchase'),
  getSubscriptionsByEmail: () => getFirebaseFunctionUrl('getSubscriptionsByEmail'),
  cancelSubscription: () => getFirebaseFunctionUrl('cancelSubscription'),
  deleteAccount: () => getFirebaseFunctionUrl('deleteAccount'),
  validateCoupon: () => getFirebaseFunctionUrl('validateCoupon'),
  redeemCoupon: () => getFirebaseFunctionUrl('redeemCoupon'),
  // Parent <-> child connection
  createConnectionCode: () => getFirebaseFunctionUrl('createConnectionCode'),
  getConnectionStatus: () => getFirebaseFunctionUrl('getConnectionStatus'),
  linkParentToChild: () => getFirebaseFunctionUrl('linkParentToChild'),
  linkChildToParent: () => getFirebaseFunctionUrl('linkChildToParent'),
  unlinkParentChild: () => getFirebaseFunctionUrl('unlinkParentChild'),
  inviteFamilyByEmail: () => getFirebaseFunctionUrl('inviteFamilyByEmail'),
  removeFamilyMember: () => getFirebaseFunctionUrl('removeFamilyMember'),
  // Push notifications (FCM)
  saveFcmToken: () => getFirebaseFunctionUrl('saveFcmToken'),
  sendNotification: () => getFirebaseFunctionUrl('sendNotification'),
  broadcastNotification: () => getFirebaseFunctionUrl('broadcastNotification'),
  // Deferred deep linking
  createDeepLink: () => getFirebaseFunctionUrl('createDeepLink'),
};
